import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SocialIcon from './SocialIcon';
import LocationIcon from './LocationIcon';
import './Footer.css';

const Footer = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const currentYear = new Date().getFullYear();

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleLinkClick = () => {
    // Scroll to top when clicking footer links
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  const footerLinks = {
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Leadership', path: '/leadership' },
      { name: 'Experience & Portfolio', path: '/portfolio' },
      { name: 'Services', path: '/services' }
    ],
    services: [
      { name: 'Construction Services', path: '/services' },
      { name: 'Property Development', path: '/services' },
      { name: 'Consultancy & Advisory', path: '/services' },
      { name: 'Real Estate Services', path: '/services' }
    ],
    support: [
      { name: 'Contact Us', path: '/contact' },
      { name: 'Why Choose Us', path: '/why-choose-us' },
      { name: 'Testimonials', path: '/testimonials' },
      { name: 'Get Consultation', path: '/consultation' }
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Disclaimer', path: '/disclaimer' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', url: 'https://facebook.com/vibhacorporation', color: '#1877F2' },
    { name: 'YouTube', url: 'https://youtube.com/@vibhacorporation', color: '#FF0000' },
    { name: 'Instagram', url: 'https://instagram.com/vibhacorporation', color: '#E4405F' },
    { name: 'X', url: 'https://x.com/vibhacorporation', color: '#FFFFFF' },
    { name: 'LinkedIn', url: 'https://linkedin.com/company/vibhacorporation', color: '#0077B5' }
  ];

  return (
    <motion.footer
      className="footer"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <div className="footer-container">
        <div className="footer-main">
          <div className="container">
            <div className="footer-grid">

              {/* Company Info Section */}
              <div className="footer-section company-info">
                <div className="footer-logo">
                  <h3>Vibha Corporation & Co.</h3>
                  <p className="tagline">Building Dreams, Creating Value</p>
                </div>

                <p className="company-description">
                  Your trusted partner in construction and real estate excellence across Tamil Nadu.
                  With nearly two decades of proven expertise and transactions worth over ₹5,500 Crores.
                </p>

                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      className="social-link"
                      aria-label={social.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: social.color }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <SocialIcon name={social.name} />
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Quick Links Section */}
              <div className="footer-section">
                <button
                  className="footer-accordion-toggle"
                  onClick={() => toggleSection('quick-links')}
                  aria-expanded={expandedSection === 'quick-links'}
                >
                  <h4>Quick Links</h4>
                  <span className="accordion-icon">
                    {expandedSection === 'quick-links' ? '−' : '+'}
                  </span>
                </button>

                <motion.div
                  className="footer-links"
                  initial={false}
                  animate={{
                    height: expandedSection === 'quick-links' ? 'auto' : 0,
                    opacity: expandedSection === 'quick-links' ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="footer-links-content">
                    <Link to="/" className="footer-link" onClick={handleLinkClick}>Home</Link>
                    <Link to="/about" className="footer-link" onClick={handleLinkClick}>About Us</Link>
                    <Link to="/services" className="footer-link" onClick={handleLinkClick}>Services</Link>
                    <Link to="/gallery" className="footer-link" onClick={handleLinkClick}>Gallery</Link>
                    <Link to="/projects" className="footer-link" onClick={handleLinkClick}>Projects</Link>
                    <Link to="/contact" className="footer-link" onClick={handleLinkClick}>Contact Us</Link>
                  </div>
                </motion.div>

                {/* Desktop version */}
                <div className="footer-links-desktop">
                  <h4>Quick Links</h4>
                  <Link to="/" className="footer-link" onClick={handleLinkClick}>Home</Link>
                  <Link to="/about" className="footer-link" onClick={handleLinkClick}>About Us</Link>
                  <Link to="/services" className="footer-link" onClick={handleLinkClick}>Services</Link>
                  <Link to="/gallery" className="footer-link" onClick={handleLinkClick}>Gallery</Link>
                  <Link to="/projects" className="footer-link" onClick={handleLinkClick}>Projects</Link>
                  <Link to="/contact" className="footer-link" onClick={handleLinkClick}>Contact Us</Link>
                </div>
              </div>

              {/* Services Section */}
              <div className="footer-section">
                <button
                  className="footer-accordion-toggle"
                  onClick={() => toggleSection('services')}
                  aria-expanded={expandedSection === 'services'}
                >
                  <h4>Our Services</h4>
                  <span className="accordion-icon">
                    {expandedSection === 'services' ? '−' : '+'}
                  </span>
                </button>

                <motion.div
                  className="footer-links"
                  initial={false}
                  animate={{
                    height: expandedSection === 'services' ? 'auto' : 0,
                    opacity: expandedSection === 'services' ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="footer-links-content">
                    {footerLinks.services.map((service, index) => (
                      <Link key={index} to={service.path} className="footer-link" onClick={handleLinkClick}>
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>

                {/* Desktop version */}
                <div className="footer-links-desktop">
                  <h4>Our Services</h4>
                  {footerLinks.services.map((service, index) => (
                    <Link key={index} to={service.path} className="footer-link" onClick={handleLinkClick}>
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact Info Section */}
              <div className="footer-section">
                <button
                  className="footer-accordion-toggle"
                  onClick={() => toggleSection('contact')}
                  aria-expanded={expandedSection === 'contact'}
                >
                  <h4>Contact Info</h4>
                  <span className="accordion-icon">
                    {expandedSection === 'contact' ? '−' : '+'}
                  </span>
                </button>

                <motion.div
                  className="footer-contact"
                  initial={false}
                  animate={{
                    height: expandedSection === 'contact' ? 'auto' : 0,
                    opacity: expandedSection === 'contact' ? 1 : 0
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-icon">📞</span>
                      <div>
                        <strong>Ramsharan S: 7358666389</strong><br />
                        <strong>Dinesh Kumar: 8870432644</strong><br />
                        <strong>Sundar Rajan Jagannathan: 9884875641</strong>
                      </div>
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">✉️</span>
                      <div>
                        <strong>ramsharan@vibhacorporation.in</strong><br />
                        <strong>dinesh@vibhacorporation.in</strong><br />
                        <strong>srj@vibhacorporation.in</strong>
                      </div>
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">
                        <LocationIcon size={20} title="Registered office location" />
                      </span>
                      <div>
                        <strong>Vibha Corporation & Co</strong>
                        <br />
                        <span>Old No: 50, New No:8 Plot No :12, Niramaya Flats, 3rd floor</span>
                        <br />
                        <span>Janakiraman Street, West Mambalam, Chennai-600033</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Desktop version */}
                <div className="footer-contact-desktop">
                  <h4>Contact Info</h4>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-icon">📞</span>
                      <div>
                        <strong>Ramsharan S: 7358666389</strong><br />
                        <strong>Dinesh Kumar: 8870432644</strong><br />
                        <strong>Sundar Rajan Jagannathan: 9884875641</strong>
                      </div>
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">✉️</span>
                      <div>
                        <strong>ramsharan@vibhacorporation.in</strong><br />
                        <strong>dinesh@vibhacorporation.in</strong><br />
                        <strong>srj@vibhacorporation.in</strong>
                      </div>
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">
                        <LocationIcon size={20} title="Registered office location" />
                      </span>
                      <div>
                        <strong>Vibha Corporation & Co</strong>
                        <br />
                        <span>Old No: 50, New No:8 Plot No :12, Niramaya Flats, 3rd floor</span>
                        <br />
                        <span>Janakiraman Street, West Mambalam, Chennai-600033</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="container">
            <div className="footer-bottom-content">
              <div className="copyright">
                <p>&copy; 2025 Vibha Corporation & Co. | All Rights Reserved | Built with Excellence</p>
              </div>

              <div className="footer-legal">
                {footerLinks.legal.map((link, index) => (
                  <Link key={index} to={link.path} className="legal-link" onClick={handleLinkClick}>
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;